import { useState, useEffect } from 'react';
import { Mail, Sparkles, Loader2, X, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'atw:leadEmail';

/**
 * EmailGate — modal that requires an email before letting the user download.
 * Remembers users across visits (localStorage). Sends leads to /api/leads.
 *
 * Usage:
 *   const gate = useEmailGate('flight-itinerary');
 *   ...
 *   <Button onClick={() => gate.run(downloadFn)}>Download</Button>
 *   <EmailGate {...gate} />
 */

export function useEmailGate(source) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const run = (fn) => {
    const known = getStoredEmail();
    if (known) {
      // already captured — just run
      fn(known);
      return;
    }
    setPending(() => fn);
    setOpen(true);
  };

  const close = () => setOpen(false);
  const complete = (email) => {
    setStoredEmail(email);
    setOpen(false);
    if (pending) {
      pending(email);
      setPending(null);
    }
  };

  return { open, close, complete, source, run };
}

export default function EmailGate({ open, close, complete, source }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [saveWarn, setSaveWarn] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setDone(false);
      setLoading(false);
      setSaveWarn(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isValidEmail(email)) return;
    setLoading(true);
    setSaveWarn(false);
    try {
      const r = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined, source }),
      });
      if (!r.ok) {
        setSaveWarn(true);
        console.warn('[EmailGate] /api/leads failed', r.status);
      }
    } catch (err) {
      setSaveWarn(true);
      console.warn('[EmailGate] network error saving lead', err);
    }
    setDone(true);
    setTimeout(() => complete(email), 700);
  };

  return (
    <div
      data-testid="email-gate-overlay"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(12px)' }}
    >
      <div
        data-testid="email-gate-modal"
        className="relative w-full max-w-md rounded-3xl overflow-hidden text-white glass-heavy"
        style={{
          background:
            'radial-gradient(600px 300px at 20% 0%, rgba(0,240,255,0.18) 0%, transparent 60%),' +
            'radial-gradient(600px 300px at 100% 100%, rgba(255,0,127,0.15) 0%, transparent 60%),' +
            'linear-gradient(180deg, rgba(3,3,3,0.95), rgba(10,10,20,0.98))',
          boxShadow:
            '0 40px 100px -30px rgba(0,240,255,0.4), 0 20px 60px -20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={close}
          data-testid="email-gate-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 flex items-center justify-center transition"
        >
          <X size={14} />
        </button>

        <div className="p-8 pb-6">
          <div className="flex justify-center mb-4">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
                boxShadow: '0 10px 30px -10px rgba(0,240,255,0.5)',
              }}
            >
              {done ? (
                <CheckCircle2 size={26} className="text-black" strokeWidth={2.5} />
              ) : (
                <Sparkles size={22} className="text-black" strokeWidth={2.5} />
              )}
            </div>
          </div>

          {!done ? (
            <>
              <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight text-center leading-tight">
                One tiny step
                <br />
                <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
                  before you download.
                </span>
              </h3>
              <p className="mt-3 text-center text-sm text-white/65 leading-relaxed">
                Drop your email &mdash; we&rsquo;ll send you the file <em>and</em> unlock
                unlimited downloads on all 6 free tools. No spam. Unsubscribe anytime.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <input
                  data-testid="email-gate-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full h-12 px-4 rounded-xl glass focus:border-[#00F0FF]/50 outline-none text-sm text-white placeholder:text-white/40"
                />
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    data-testid="email-gate-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@work.com"
                    autoFocus
                    className="w-full h-12 pl-10 pr-4 rounded-xl glass focus:border-[#00F0FF]/50 outline-none text-sm text-white placeholder:text-white/40"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="email-gate-submit"
                  disabled={loading || !isValidEmail(email)}
                  className="w-full h-12 rounded-xl font-bold text-black"
                  style={{
                    background: 'linear-gradient(90deg, #00F0FF 0%, #ffffff 50%, #FF007F 100%)',
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} /> Unlocking…
                    </>
                  ) : (
                    'Unlock download →'
                  )}
                </Button>
              </form>
              <div className="mt-4 flex items-center justify-center gap-3 text-[10px] uppercase tracking-widest text-white/40 font-mono">
                <span>Secure</span>
                <span className="h-1 w-1 rounded-full bg-white/25" />
                <span>GDPR-safe</span>
                <span className="h-1 w-1 rounded-full bg-white/25" />
                <span>No spam</span>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <h3 className="font-display font-black text-2xl text-white tracking-tight">
                You&rsquo;re in ✨
              </h3>
              <p className="mt-2 text-sm text-white/65">Your download is starting…</p>
              {saveWarn && (
                <p
                  data-testid="email-gate-save-warn"
                  className="mt-3 text-[11px] text-amber-300/80 leading-relaxed"
                >
                  Note: we couldn&rsquo;t confirm the save just now, but your download
                  continues normally.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
}
function getStoredEmail() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}
function setStoredEmail(email) {
  try {
    localStorage.setItem(STORAGE_KEY, email);
  } catch {
    // ignore
  }
}
